/**
 * 兌換報表 — **我方員工** realm（`/api/admin/redeem-reports/*`；批107 項5）。
 *
 * 型別集中在這個檔（代理商版 `api/resellerRedeemReports.ts` 直接 import 它們）：
 * 兩個 realm 的回應是**同一個形狀**，差別只在幾個欄位存不存在——
 *
 * | 欄位 | 我方 | 代理商 |
 * |---|---|---|
 * | `stats.resellers`（代理商排行） | 陣列 | **`null`**（伺服器端就不算） |
 * | 明細的 `issued_by`／`voided_by` | 有 | 無（改成 `*_label`） |
 * | 明細的 `client_ip`／`reseller_name` | 有 | 無 |
 *
 * 所以那幾個欄位在型別上都是**選填**，元件一律用 `?.`／`??` 取值，不要用非空斷言。
 *
 * ★ **不放進 `api/ls.ts`**：那個檔是我方 realm 的大雜燴，而報表兩個 realm 共用同一支
 *   元件，型別放在一起才不會有人在其中一邊偷加欄位。
 */
import { api, qs } from './client'

export interface ReportWindow {
  from: string
  to: string
  /** 後端回的報表時區（`settings.report_timezone`）——畫面上要標出來，否則使用者會以為是 UTC。 */
  timezone: string
}

/** 三張 KPI 卡＋領用 cohort 的狀態拆解。 */
export interface ReportKpi {
  /** 期間內**發出**的子碼張數（cohort；共用碼不算）。 */
  issued: number
  /** 上面那些碼**現在**已兌換／已作廢／未兌換各幾張。 */
  issued_redeemed: number
  issued_voided: number
  issued_unused: number
  /** 期間內發生的**兌換次數**（事件語意，與上面的 cohort 是兩件事）。 */
  redeemed: number
  /** 期間內兌換過的**不重複客戶數**。 */
  customers: number
}

export interface DailyPoint {
  /** `YYYY-MM-DD`（報表時區的日期，後端已補零到整個區間）。 */
  date: string
  issued: number
  redeemed: number
}

export interface CampaignStatRow {
  campaign_id: string
  name: string
  product: string
  kind: string
  code_mode: string
  status: string
  quantity: number | null
  issued: number
  issued_redeemed: number
  issued_voided: number
  issued_unused: number
  /** 期間內的兌換次數。 */
  redeemed: number
  /** 這檔活動**歷來**的兌換次數（共用碼的兌換率母數用得到）。 */
  redeemed_total: number
  /** 0–1；`null`＝算不出母數（共用碼無限量／期間內零發放），畫面顯示「—」。 */
  redeem_rate: number | null
  reseller_id?: string | null
  reseller_name?: string | null
}

export interface ChannelStat {
  channel: string
  count: number
}

export interface ResellerStat {
  /** `null`＝我方直營。 */
  reseller_id: string | null
  reseller_name: string | null
  redeemed: number
}

export interface RedeemStats {
  window: ReportWindow
  kpi: ReportKpi
  daily: DailyPoint[]
  campaigns: CampaignStatRow[]
  channels: ChannelStat[]
  /** **代理商 realm 恆為 `null`**（排行只有我方看得到，後端就不算）。 */
  resellers: ResellerStat[] | null
  /** 活動數超過後端上限（200）而被截斷。 */
  campaigns_truncated: boolean
}

export interface IssuedRow {
  id: string
  campaign_id: string
  campaign_name: string
  product: string
  kind: string
  /** **遮罩過**的代碼（`EXPO26-••••-AB12`）。完整代碼只在單一活動的代碼頁。 */
  masked_code: string
  status: string
  issued_at: string
  issued_realm: string | null
  distributed_to_customer_id: string | null
  distributed_to_customer_name: string | null
  distributed_note: string | null
  voided_at: string | null
  redeemed_at: string | null
  // --- 我方 realm 才有 ---
  issued_by?: string | null
  voided_by?: string | null
  reseller_id?: string | null
  reseller_name?: string | null
  // --- 代理商 realm 才有（我方 email 已經被後端換成標籤）---
  issued_by_label?: string | null
  voided_by_label?: string | null
}

export interface RedemptionRow {
  id: string
  campaign_id: string
  campaign_name: string | null
  product: string
  kind: string
  masked_code: string
  redeemed_at: string
  channel: string
  customer_id: string | null
  customer_name: string | null
  instance_id: string
  hostname: string | null
  public_url: string | null
  actor_email: string | null
  actor_name: string | null
  actor_verified: boolean
  app_version: string | null
  effect_summary: Record<string, unknown>
  new_license_id: string | null
  addon_id: string | null
  // --- 我方 realm 才有 ---
  client_ip?: string | null
  reseller_id?: string | null
  reseller_name?: string | null
}

export interface ReportPage<T> {
  items: T[]
  next_cursor: string | null
  window: ReportWindow
}

/** 共用過濾條件。`reseller_id` 只有我方版吃（代理商版的分流會把它丟掉）。 */
export interface ReportQuery {
  from?: string
  to?: string
  product?: string
  reseller_id?: string
  campaign_id?: string
  kind?: string
  customer_id?: string
}

/** 領用明細專屬：代碼狀態與發放來源。 */
export interface IssuedQuery extends ReportQuery {
  status?: string
  issued_realm?: string
  limit?: number
  cursor?: string | null
}

/** 使用明細專屬：兌換通路。 */
export interface RedemptionQuery extends ReportQuery {
  channel?: string
  limit?: number
  cursor?: string | null
}

const P = '/api/admin/redeem-reports'

/** 把查詢物件攤成 query string（`undefined`／空字串一律不送，見 `client.qs`）。 */
export function reportQs(
  q: Record<string, string | number | null | undefined>,
  extra: Record<string, string | undefined> = {},
): string {
  const out: Record<string, string | undefined> = { ...extra }
  for (const [k, v] of Object.entries(q)) {
    out[k] = v === undefined || v === null || v === '' ? undefined : String(v)
  }
  return qs(out)
}

export const reportStats = (q: ReportQuery = {}) =>
  api<RedeemStats>(`${P}/stats${reportQs(q as Record<string, string | undefined>)}`)

export const reportIssued = (q: IssuedQuery = {}) =>
  api<ReportPage<IssuedRow>>(`${P}/issued${reportQs(q as Record<string, string | undefined>)}`)

export const reportRedemptions = (q: RedemptionQuery = {}) =>
  api<ReportPage<RedemptionRow>>(
    `${P}/redemptions${reportQs(q as Record<string, string | undefined>)}`,
  )

/**
 * CSV 匯出——回**原始 `Response`**（`raw: true`），呼叫端自己轉 blob 下載。
 *
 * ★ 不能用 `<a href>` 直接連過去：這支端點要 `Authorization` 標頭，而瀏覽器的
 *   導覽請求帶不了它（token 存在 localStorage，不是 cookie）。
 * ★ 回應可能帶 `X-Export-Truncated: 1`（達到後端五萬列上限）——呼叫端要讀它並提示，
 *   否則使用者會拿到一份「看起來完整」的殘缺報表。
 */
export const reportIssuedCsv = (q: IssuedQuery = {}) =>
  api<Response>(
    `${P}/issued${reportQs(q as Record<string, string | undefined>, { format: 'csv' })}`,
    { raw: true },
  )

export const reportRedemptionsCsv = (q: RedemptionQuery = {}) =>
  api<Response>(
    `${P}/redemptions${reportQs(q as Record<string, string | undefined>, { format: 'csv' })}`,
    { raw: true },
  )
